import { supabase } from '../supabaseClient';
import type { LandingFaqDraft, LandingFaqItem } from '../types/landingFaq';

const FAQ_CACHE_KEY = 'curriculum-landing-faq.v3';

interface CachedFaq {
  data: LandingFaqItem[];
  /** 행 메타 지문 — head 비교용 */
  version: string;
  lastUpdated: string;
}

interface FaqVersionRow {
  id: string;
  sort_order: number;
  is_active: boolean;
  updated_at: string;
  category: string;
}

function normalizeFaqRow(row: LandingFaqItem): LandingFaqItem {
  return {
    ...row,
    category: typeof row.category === 'string' ? row.category.trim() : '',
  };
}

function maxUpdatedAt(rows: { updated_at: string }[]): string {
  return rows.reduce(
    (max, r) => (r.updated_at > max ? r.updated_at : max),
    rows[0]?.updated_at ?? '',
  );
}

export function buildFaqCacheVersion(rows: FaqVersionRow[]): string {
  return [...rows]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map(
      (r) =>
        `${r.id}:${r.sort_order}:${r.is_active ? 1 : 0}:${(r.category ?? '').trim()}:${r.updated_at}`,
    )
    .join('|');
}

export function loadCachedFaq(): CachedFaq | null {
  try {
    const raw = localStorage.getItem(FAQ_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedFaq;
    if (!parsed?.version || !Array.isArray(parsed.data)) return null;
    return {
      ...parsed,
      data: (parsed.data ?? []).map((item) => normalizeFaqRow(item)),
    };
  } catch {
    return null;
  }
}

export function saveFaqCache(data: LandingFaqItem[], version?: string): void {
  try {
    const resolvedVersion =
      version ??
      buildFaqCacheVersion(
        data.map((item) => ({
          id: item.id,
          sort_order: item.sort_order,
          is_active: item.is_active,
          updated_at: item.updated_at,
          category: item.category ?? '',
        })),
      );
    const cached: CachedFaq = {
      data: data.map((item) => normalizeFaqRow(item)),
      version: resolvedVersion,
      lastUpdated: maxUpdatedAt(data) || new Date().toISOString(),
    };
    localStorage.setItem(FAQ_CACHE_KEY, JSON.stringify(cached));
  } catch {
    /* ignore */
  }
}

export function clearFaqCache(): void {
  try {
    localStorage.removeItem(FAQ_CACHE_KEY);
    localStorage.removeItem('curriculum-landing-faq.v2');
    localStorage.removeItem('curriculum-landing-faq.v1');
  } catch {
    /* ignore */
  }
}

export async function fetchFaqCacheVersion(): Promise<string | null> {
  const { data, error } = await supabase
    .from('landing_page_faq')
    .select('id, sort_order, is_active, updated_at, category');

  if (error) throw error;
  return buildFaqCacheVersion(
    ((data ?? []) as FaqVersionRow[]).map((row) => ({
      ...row,
      category: typeof row.category === 'string' ? row.category : '',
    })),
  );
}

/** 공개 랜딩용 — 활성 항목만 (풀 fetch) */
export async function getActiveFaqItems(): Promise<LandingFaqItem[]> {
  const { data, error } = await supabase
    .from('landing_page_faq')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return ((data ?? []) as LandingFaqItem[]).map((item) => normalizeFaqRow(item));
}

/**
 * 공개 Q&A: LS 캐시 + head 버전 비교 후 변경 시에만 풀 조회
 */
export async function getActiveFaqItemsWithCache(options?: {
  forceRefresh?: boolean;
}): Promise<{ items: LandingFaqItem[]; fromCache: boolean }> {
  const cached = loadCachedFaq();

  if (!options?.forceRefresh && cached?.version && cached.data?.length) {
    try {
      const remoteVersion = await fetchFaqCacheVersion();
      if (remoteVersion != null && remoteVersion === cached.version) {
        return {
          items: cached.data
            .filter((x) => x.is_active)
            .sort((a, b) => a.sort_order - b.sort_order),
          fromCache: true,
        };
      }
    } catch {
      /* fall through */
    }
  }

  const { data, error } = await supabase
    .from('landing_page_faq')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  const all = ((data ?? []) as LandingFaqItem[]).map((item) => normalizeFaqRow(item));
  const version = buildFaqCacheVersion(
    all.map((item) => ({
      id: item.id,
      sort_order: item.sort_order,
      is_active: item.is_active,
      updated_at: item.updated_at,
      category: item.category ?? '',
    })),
  );
  saveFaqCache(all, version);
  return {
    items: all.filter((x) => x.is_active).sort((a, b) => a.sort_order - b.sort_order),
    fromCache: false,
  };
}

/** 관리자 CMS용 — 전체 */
export async function getAllFaqItems(): Promise<LandingFaqItem[]> {
  const { data, error } = await supabase
    .from('landing_page_faq')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return ((data ?? []) as LandingFaqItem[]).map((item) => normalizeFaqRow(item));
}

export async function createFaqItem(input: {
  category?: string;
  question: string;
  answer: string;
  sort_order: number;
  is_active?: boolean;
}): Promise<LandingFaqItem> {
  const { data, error } = await supabase
    .from('landing_page_faq')
    .insert({
      category: (input.category ?? '').trim(),
      question: input.question,
      answer: input.answer,
      sort_order: input.sort_order,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  clearFaqCache();
  return normalizeFaqRow(data as LandingFaqItem);
}

export async function updateFaqItem(
  id: string,
  updates: Partial<Pick<LandingFaqItem, 'category' | 'question' | 'answer' | 'sort_order' | 'is_active'>>,
): Promise<LandingFaqItem> {
  const { data, error } = await supabase
    .from('landing_page_faq')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  clearFaqCache();
  return normalizeFaqRow(data as LandingFaqItem);
}

export async function deleteFaqItem(id: string): Promise<void> {
  const { error } = await supabase.from('landing_page_faq').delete().eq('id', id);
  if (error) throw error;
  clearFaqCache();
}

/** 드래프트 목록을 순서·내용 기준으로 일괄 저장 */
export async function saveFaqDrafts(
  drafts: LandingFaqDraft[],
  deletedIds: string[],
): Promise<LandingFaqItem[]> {
  for (const id of deletedIds) {
    await deleteFaqItem(id);
  }

  for (let i = 0; i < drafts.length; i += 1) {
    const draft = drafts[i];
    const sort_order = i + 1;
    const category = draft.category.trim();
    const question = draft.question.trim() || `질문 ${sort_order}`;
    const answer = draft.answer.trim();

    if (draft.isNew) {
      await createFaqItem({
        category,
        question,
        answer,
        sort_order,
        is_active: draft.is_active,
      });
    } else {
      await updateFaqItem(draft.id, {
        category,
        question,
        answer,
        sort_order,
        is_active: draft.is_active,
      });
    }
  }

  return getAllFaqItems();
}
